// WheelThumb — the ONE shared small-Wheel thumbnail (§12 WHEEL-WITHOUT-PORTRAIT
// LAW). WL-019. Renders the canonical BrandWheel ring (G-19: never a hand-rolled
// donut) with a REAL center every time:
//   • a real portrait photo when the Wheel has one (`portrait` URL), else
//   • the colored iYuga company logo — never a hollow center, never an emoji.
//
// The /wheel/user/analytics/ + hall-of-fame endpoints do not return a portrait
// image today, so live rows fall to the iYuga-logo branch. `portrait` is the
// extension point: when WL-040 / a Wheel-image field lands, pass the URL and the
// real photo renders inset automatically — no caller change.
import React, { useState } from "react";
import styled from "styled-components";
import BrandWheel from "../BrandWheel";
import iyugaLogo from "@assets/images/iyugalogo.svg";

const Frame = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
`;

// Center disc geometry mirrors BrandWheel: white Portrait disc ~0.675 of the
// outer diameter. We inset the media to 0.62 so the colored rim always shows.
const Center = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${(p) => Math.round(p.$size * 0.62)}px;
  height: ${(p) => Math.round(p.$size * 0.62)}px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;

  img {
    width: 100%;
    height: 100%;
    /* Real photo fills the disc; the (wider) logo is contained + padded. */
    object-fit: ${(p) => (p.$isLogo ? "contain" : "cover")};
    padding: ${(p) => (p.$isLogo ? "14%" : "0")};
    box-sizing: border-box;
    display: block;
  }
`;

const WheelThumb = ({ portrait, name, size = 40, className }) => {
  // §12 NEVER-BROKEN-CENTRE: `portrait` is a caller-supplied URL, so a truthy but
  // DEAD one (expired presigned S3 link, 403/404, ORB block — the class measured
  // live on Explore, RP-E2.12/RP-F1.2) would paint the browser's broken-image
  // glyph inside the disc. A truthiness check cannot see a dead URL, so track the
  // failed src (not a boolean) and degrade to the same iYuga-logo branch as "no
  // portrait at all". Keyed on the src so a recycled row with a DIFFERENT wheel's
  // portrait auto-clears the stale error instead of needing a reset effect —
  // same shape as ExploreWheelCard's `erroredPreviewSrc`.
  const [erroredSrc, setErroredSrc] = useState(null);
  const portraitFailed = Boolean(portrait) && erroredSrc === portrait;
  const isLogo = !portrait || portraitFailed;
  const src = isLogo ? iyugaLogo : portrait;
  const label = name ? `${name} Wheel` : "iYuga Wheel";
  return (
    <Frame $size={size} className={className} role="img" aria-label={label}>
      <BrandWheel size={size} aria-hidden="true" />
      <Center $size={size} $isLogo={isLogo}>
        <img
          src={src}
          alt=""
          aria-hidden="true"
          // BUG-discover-442c7f: during a window resize the feed re-lays-out and
          // these discs re-evaluate; a lazy-loaded center left the portrait-less
          // (brand-logo) disc visibly EMPTY mid-resize (measured headed
          // 2026-08-16, interval frame rz-08: blank white center while settled
          // frames show the logo). The logo is a bundled local asset — lazy
          // buys nothing and costs the dropout — so the logo branch loads
          // eagerly; real remote portraits keep lazy.
          loading={isLogo ? "eager" : "lazy"}
          onError={() => portrait && setErroredSrc(portrait)}
        />
      </Center>
    </Frame>
  );
};

// A reusable form/poll lives on many Wheels — show up to `max` overlapping
// WheelThumbs + a "+N" disc in a FIXED slot (the cell never grows no matter how
// many placements). Each thumb is the canonical WheelThumb above, so the
// never-hollow guarantee holds for every one (§12 · WL-019). Additive extension.
const StackWrap = styled.span`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
`;

const StackItem = styled.span`
  display: inline-flex;
  border-radius: 50%;
  box-shadow: 0 0 0 2px #fff;
  margin-left: ${(p) => (p.$first ? 0 : -p.$overlap)}px;
  z-index: ${(p) => p.$z};
`;

const Overflow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  margin-left: ${(p) => -p.$overlap}px;
  z-index: 0;
  border-radius: 50%;
  background: #ede7fb;
  color: #5b21b6;
  font-family: "DM Sans", sans-serif;
  font-weight: 700;
  font-size: ${(p) => Math.max(9, Math.round(p.$size * 0.3))}px;
  box-shadow: 0 0 0 2px #fff;
`;

export const WheelThumbStack = ({ wheels = [], size = 40, max = 3 }) => {
  const shown = wheels.slice(0, max);
  const overflow = wheels.length - shown.length;
  const overlap = Math.round(size * 0.42);
  return (
    <StackWrap>
      {shown.map((w, i) => (
        <StackItem
          key={w && w.id != null ? w.id : i}
          $first={i === 0}
          $overlap={overlap}
          $z={shown.length - i}
        >
          <WheelThumb portrait={w && w.portrait} name={w && w.name} size={size} />
        </StackItem>
      ))}
      {overflow > 0 && (
        <Overflow
          $size={size}
          $overlap={overlap}
          aria-label={`plus ${overflow} more Wheels`}
        >
          +{overflow}
        </Overflow>
      )}
    </StackWrap>
  );
};

export default WheelThumb;
